import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';
import moment from 'moment';

Font.register({
  family: 'Mona-Sans',
  fonts: [
    {src: '/MonaSans-Regular.ttf'},
    {src: '/MonaSans-Medium.ttf', fontWeight: 'medium'},
    {src: '/MonaSans-Bold.ttf', fontWeight: 'bold'},
    {src: '/MonaSans-SemiBold.ttf', fontWeight: 'semibold'},
  ],
});

const styles = StyleSheet.create({
  page: {
    paddingVertical: 25,
    paddingHorizontal: 30,
    fontFamily: 'Mona-Sans',
    fontSize: 10,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  logo: {
    width: 121,
    height: 27,
  },
  headerRef: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  labelIdGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerRefName: {
    color: '#161616',
    fontSize: 10,
    fontWeight: 'medium',
    textTransform: 'uppercase',
  },
  headerRefID: {
    color: '#000000',
    fontSize: 12,
    fontWeight: 'medium',
  },
  statusBadge: {
    fontSize: 8,
    fontWeight: 'medium',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 24,
    textTransform: 'uppercase',
  },
  statusSuccess: {
    color: '#4A878D',
    backgroundColor: '#E2F5F6',
  },
  statusBlock: {
    color: '#C0392B',
    backgroundColor: '#F9EBEB',
  },
  statusPending: {
    color: '#B7791F',
    backgroundColor: '#FEFCBF',
  },
  timestamp: {
    fontSize: 10,
    color: '#161616',
    fontWeight: 'medium',
    textTransform: 'uppercase',
  },
  section: {
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'medium',
    color: '#0B1C56',
    backgroundColor: '#EEF3FC',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: 10,
    paddingBottom: 10,
    marginBottom: 4,
  },
  sectionContent: {
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: 8,
    paddingBottom: 8,
    border: '1px solid #EEF3FC',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
  },
  gridContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 12,
  },
  dataPair: {
    flexGrow: 1,
    flexShrink: 0,
    paddingRight: 15,
    marginBottom: 10,
  },
  dataKey: {
    fontSize: 10,
    color: '#161616',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginBottom: 5,
  },
  dataValue: {
    fontSize: 12,
    fontWeight: 'medium',
    color: '#4E4E4E',
    lineHeight: 1.4,
  },
  footer: {
    flexDirection: 'column',
    marginTop: 30,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#E4E4E6',
    alignItems: 'center',
  },
  footerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 8,
  },
  footerText: {
    fontSize: 9,
    color: '#858DAA',
    fontWeight: 'bold',
    textAlign: 'center',
    width: '33%',
  },
});

const DataPair = ({label, value, style = {}}) => (
  <View style={[styles.dataPair, style]}>
    <Text style={styles.dataKey}>{label}</Text>
    <Text style={styles.dataValue}>{value || '-'}</Text>
  </View>
);

const formatMoney = (currency, amount) => {
  if (!amount) return '-';
  return `${currency ? currency.toUpperCase() : ''} ${amount}`;
};

export const CtrDocument = ({data, logoImage, userName, userDetails}) => {
  const txData = data?.transaction_data || {};
  const ev = txData?.event || {};
  const evTx = ev?.transaction || {};
  const evUser = ev?.user || {};
  const evAddress = ev?.address || {};

  const entityDetails =
    userDetails?.company?.setup_config?.entity_details || {};

  const currentDate = moment().format('DD/MM/YYYY, hh:mmA').toUpperCase();
  const txDate = data?.transaction_time
    ? moment(data.transaction_time).format('DD/MM/YYYY, hh:mmA')
    : currentDate;

  const fullAddress = evAddress
    ? [evAddress.street, evAddress.city, evAddress.country]
        .filter(Boolean)
        .join(', ')
    : '-';

  const getStatusStyle = status => {
    const normalizedStatus = status ? status.toUpperCase() : '';

    let colorStyle = styles.statusPending;

    if (normalizedStatus === 'ALLOW') {
      colorStyle = styles.statusSuccess;
    } else if (normalizedStatus === 'BLOCK') {
      colorStyle = styles.statusBlock;
    }

    return [styles.statusBadge, colorStyle];
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} wrap={false}>
          {logoImage && <Image style={styles.logo} src={logoImage} />}
          <DataPair
            label="CHANNEL"
            value={evTx?.channel ? evTx.channel.toUpperCase() : '-'}
            style={{width: '20%', flexGrow: 0}}
          />
        </View>

        <View style={styles.headerRef} wrap={false}>
          <View style={styles.labelIdGroup}>
            <Text style={styles.headerRefName}>ProfiledRisk Profile ID</Text>
            <Text style={styles.headerRefID}>{data?.user_id || '-'}</Text>
          </View>

          <Text style={getStatusStyle(data?.final_outcome)}>
            {data?.final_outcome || 'PENDING'}
          </Text>
        </View>

        <View style={styles.headerRef} wrap={false}>
          <View style={styles.labelIdGroup}>
            <Text style={styles.headerRefName}>ProfiledRisk Event ID</Text>
            <Text style={styles.headerRefID}>{data?.id || '-'}</Text>
          </View>
          <Text style={styles.timestamp}>{txDate}</Text>
        </View>

        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Transaction Details</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="TOTAL CASH AMOUNT"
                value={formatMoney(evTx?.currency, evTx?.amount)}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="TRANSACTION TYPE"
                value={evTx?.type}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="CURRENCY TYPE"
                value={evTx?.currency ? evTx.currency.toUpperCase() : '-'}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="CHANNEL"
                value={evTx?.channel ? evTx.channel.toUpperCase() : '-'}
                style={{width: '50%', flexGrow: 0}}
              />
              <DataPair
                label="WAS THE AMOUNT AGGREGATED?"
                value={'-'}
                style={{width: '50%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>
            Person conducting the cash transaction
          </Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="FULL NAME"
                value={evUser?.name}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ADDRESS"
                value={fullAddress}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ID TYPE"
                value={'-'}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ID NUMBER"
                value={evUser?.user_id}
                style={{width: '100%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Reporting Entity</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="REPORTING ENTITY NAME"
                value={entityDetails?.reporting_entity_name}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="RENDERING ENTITY CODE"
                value={entityDetails?.rendering_entity_code}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="BRANCH/LOCATION OF TRANSACTION"
                value={entityDetails?.branch_name_or_id}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="DATE & TIME OF TRANSACTION"
                value={txDate}
                style={{width: '33%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.footer} wrap={false}>
          <View style={styles.footerTopRow}>
            <Text style={styles.footerText}>
              GENERATED BY: {(userName || 'N/A').toUpperCase()}
            </Text>
            <Text style={styles.footerText}>GENERATED: {currentDate}</Text>
            <Text style={styles.footerText}>DOWNLOADED: {currentDate}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
